<!-- EditMaterialModal.vue -->
<template>
	<transition name="modal-fade">
		<div v-if="isOpen" class="edit-modal" @click.self="handleClose">
			<div class="edit-modal__container">
				<!-- Заголовок -->
				<div class="edit-modal__header">
					<h2 class="edit-modal__title">
						{{ modalTitle }}
					</h2>
					<button
						type="button"
						class="edit-modal__close"
						@click="handleClose"
						aria-label="Закрыть"
					>
						✕
					</button>
				</div>

				<!-- Кнопки действий -->
				<div class="edit-modal__actions">
					<button
						type="button"
						class="edit-modal__action-btn edit-modal__action-btn--preview"
						@mousedown="handlePreviewIntent"
						@click="handlePreview"
					>
						👁️ Предпросмотр
					</button>
				</div>

				<!-- Контент формы -->
				<div class="edit-modal__content">
					<component
						:is="currentFormComponent"
						v-if="currentFormComponent"
						:initial-data="material"
						:current-module-id="currentModuleId"
						:available-modules="availableModules"
						:editing-test="normalizedMaterialType === 'test' ? material : null"
						:editing-lesson="
							normalizedMaterialType === 'lesson' ? material : null
						"
						:editing-practice="
							normalizedMaterialType === 'practice' ? material : null
						"
						:editing-homework="
							normalizedMaterialType === 'homework' ? material : null
						"
						@save-test="handleSave"
						@save-lesson="handleSave"
						@save-practice="handleSave"
						@save-homework="handleSave"
						@save-draft="handleSaveDraft"
						@cancel="handleClose"
					/>
					<div v-else class="edit-modal__no-form">
						<p>Форма редактирования недоступна для данного типа материала.</p>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import TestCreationForm from '../views/teacher/TeacherDashboard/DivWrapperScreen/TestCreationForm.vue'
import LessonCreationForm from '../views/teacher/TeacherDashboard/DivWrapperScreen/LessonCreationForm.vue'
import PracticeCreationForm from '../views/teacher/TeacherDashboard/DivWrapperScreen/PracticeCreationForm.vue'
import HomeworkCreationForm from '../views/teacher/TeacherDashboard/DivWrapperScreen/HomeworkCreationForm.vue'

export default {
	name: 'EditMaterialModal',
	components: {
		TestCreationForm,
		LessonCreationForm,
		PracticeCreationForm,
		HomeworkCreationForm,
	},
	props: {
		isOpen: {
			type: Boolean,
			default: false,
		},
		material: {
			type: Object,
			default: null,
		},
		materialType: {
			type: String,
			default: null,
			// Допускаем как единственное, так и множественное число, а также null
			validator: value =>
				value === null ||
				['test', 'tests', 'lesson', 'lessons', 'practice', 'homework'].includes(
					value
				),
		},
		currentModuleId: {
			type: [String, Number],
			default: null,
		},
		availableModules: {
			type: Array,
			default: () => [],
		},
	},
	emits: ['close', 'save', 'save-draft', 'preview', 'preview-intent'],
	computed: {
		// Нормализуем тип материала к единственному числу,
		// чтобы корректно подбирать компонент и заголовки
		normalizedMaterialType() {
			const map = {
				tests: 'test',
				lessons: 'lesson',
			}
			return map[this.materialType] || this.materialType
		},
		modalTitle() {
			const titles = {
				test: 'Редактирование теста',
				lesson: 'Редактирование урока',
				practice: 'Редактирование практического задания',
				homework: 'Редактирование домашнего задания',
			}
			return titles[this.normalizedMaterialType] || 'Редактирование материала'
		},
		currentFormComponent() {
			const components = {
				test: 'TestCreationForm',
				lesson: 'LessonCreationForm',
				practice: 'PracticeCreationForm',
				homework: 'HomeworkCreationForm',
			}
			return components[this.normalizedMaterialType] || null
		},
	},
	methods: {
		handleClose() {
			this.$emit('close')
		},
		handleSave(data) {
			this.$emit('save', {
				...data,
				id: this.material?.id,
				type: this.normalizedMaterialType,
			})
		},
		handleSaveDraft(data) {
			this.$emit('save-draft', {
				...data,
				id: this.material?.id,
				type: this.normalizedMaterialType,
			})
		},
		handlePreview() {
			this.$emit('preview', {
				material: this.material,
				type: this.normalizedMaterialType,
			})
		},
		handlePreviewIntent() {
			this.$emit('preview-intent')
		},
	},
	watch: {
		isOpen(newValue) {
			if (newValue) {
				document.body.style.overflow = 'hidden'
			} else {
				document.body.style.overflow = ''
			}
		},
	},
	beforeUnmount() {
		document.body.style.overflow = ''
	},
}
</script>

<style scoped>
.edit-modal {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 2000;
	padding: 20px;
	overflow-y: auto;
}

.edit-modal__container {
	background-color: #ffffff;
	border-radius: 16px;
	width: 100%;
	max-width: 900px;
	max-height: 90vh;
	overflow-y: auto;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	position: relative;
}

.edit-modal__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 24px 28px;
	border-bottom: 1px solid #e9ecef;
	position: sticky;
	top: 0;
	background-color: #ffffff;
	z-index: 10;
}

.edit-modal__title {
	font-family: 'Poppins', Helvetica;
	font-size: 24px;
	font-weight: 700;
	color: #212529;
	margin: 0;
}

.edit-modal__close {
	width: 40px;
	height: 40px;
	border: none;
	background-color: #f8f9fa;
	border-radius: 50%;
	font-size: 24px;
	color: #6c757d;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
}

.edit-modal__close:hover {
	background-color: #e9ecef;
	color: #212529;
	transform: rotate(90deg);
}

.edit-modal__actions {
	padding: 16px 28px;
	border-bottom: 1px solid #e9ecef;
	display: flex;
	gap: 12px;
	background-color: #f8f9fa;
}

.edit-modal__action-btn {
	padding: 10px 20px;
	border: 1px solid #0095ec;
	border-radius: 8px;
	background-color: #ffffff;
	color: #0095ec;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 8px;
}

.edit-modal__action-btn:hover {
	background-color: #0095ec;
	color: #ffffff;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}

.edit-modal__content {
	padding: 28px;
}

.edit-modal__no-form {
	text-align: center;
	padding: 40px 20px;
	color: rgba(0, 0, 0, 0.6);
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
}

/* Анимации */
.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
	opacity: 0;
}

.modal-fade-enter-active .edit-modal__container,
.modal-fade-leave-active .edit-modal__container {
	transition: transform 0.3s ease;
}

.modal-fade-enter-from .edit-modal__container,
.modal-fade-leave-to .edit-modal__container {
	transform: scale(0.9) translateY(-20px);
}

/* Адаптивность */
@media (max-width: 768px) {
	.edit-modal {
		padding: 10px;
	}

	.edit-modal__container {
		max-height: 95vh;
	}

	.edit-modal__header {
		padding: 20px;
	}

	.edit-modal__title {
		font-size: 20px;
	}

	.edit-modal__close {
		width: 36px;
		height: 36px;
		font-size: 20px;
	}

	.edit-modal__actions {
		padding: 12px 20px;
		flex-wrap: wrap;
	}

	.edit-modal__action-btn {
		font-size: 13px;
		padding: 8px 16px;
	}

	.edit-modal__content {
		padding: 20px;
	}
}
</style>
